import { Box, Flex, Image } from '@chakra-ui/react';
import { Heading, Text, VStack, HStack } from 'components/ui';
import { GradientButton } from './GradientButton';
import { useAffiliate } from '../../../../api/affiliates/useAffiliate';
import {
  AffiliateCareModel,
  AffiliateType,
} from '../../../../models/affiliate';
import { TRACKING_LANDING_PAGE_CTA } from '../../../OnboardingForm/constants';

const features = (affiliate) => [
  {
    title: 'Virtual visits & messaging',
    description:
      affiliate?.care_model === AffiliateCareModel.collaborative
        ? '24/7 symptom support from your care team (a provider and coach).'
        : affiliate?.care_model === AffiliateCareModel.coachOnly
          ? '24/7 symptom support from a dedicated health coach.'
          : '24/7 support from your care team and a custom digestive care plan.',
    image: '/high-five-cropped.png',
  },
  {
    title: 'Discover symptom triggers',
    description: 'Symptom and meal tracking, grocery tools, & more in the app.',
    image: '/heart-phone-cropped.png',
  },
  {
    title: 'Nutrition coaching',
    description: 'Work with a health coach to build healthy habits that last.',
    image: '/stairs.png',
  },
  {
    title: 'Mind–gut stress relief',
    description: 'Guided relaxations led by top digestive psychologists.',
    image: '/r&r.png',
  },
  {
    title: 'Teaming up with your doctor',
    description: `We keep your ${
      affiliate?.name || ''
    } provider up to date on progress.`,
    image: '/piggy_back_cropped.png',
    excluded:
      !affiliate ||
      affiliate?.affiliate_type !== AffiliateType.healthSystem ||
      affiliate?.care_model !== AffiliateCareModel.collaborative,
  },
];

export const WhatYouGet = ({ onStartOnboarding }) => {
  const { affiliate } = useAffiliate();
  return (
    <Box py={{ base: 5, lg: 10 }} maxW={{ base: '90%', xl: '70%' }} mx="auto">
      <VStack gap={6} alignItems="center" mb={6}>
        <Text
          borderWidth={1}
          borderColor="#718F9A"
          px={4}
          py={2}
          borderRadius="full"
          fontSize="sm"
          fontWeight={500}
          color="#718F9A"
        >
          What You Get With Ayble
        </Text>

        <Heading
          color={'secondary.500'}
          textAlign="center"
          fontSize={{ base: '3xl', lg: '4xl' }}
          fontWeight={400}
          maxW={{ base: '90%', lg: '100%' }}
        >
          {affiliate?.affiliate_type === AffiliateType.healthSystem
            ? 'Manage your GI symptoms, at home'
            : 'Personalized Digestive Care That Works'}
        </Heading>

        <Flex
          direction={{ base: 'column', lg: 'row' }}
          gap={{ base: 4, lg: 'auto' }}
          maxW={{ base: '100%', lg: '90%', xl: '80%' }}
          flexWrap={'wrap'}
        >
          {features(affiliate).map(
            ({ title, description, image, excluded }) => (
              <HStack
                key={title}
                p={5}
                bg="white"
                borderRadius="xl"
                shadow="md"
                justifyContent="space-between"
                alignItems="center"
                mx={'auto'}
                w={{ base: '100%', lg: '48%' }}
                my={{ base: 'auto', lg: 3 }}
                display={excluded ? 'none' : 'flex'}
              >
                <VStack maxW="70%">
                  <Heading
                    textAlign={'left'}
                    fontWeight={400}
                    fontSize={{ base: '18px', lg: '19px' }}
                    color="secondary.600"
                  >
                    {title}
                  </Heading>
                  <Text fontSize="16px" color="secondary.200" mt={1}>
                    {description}
                  </Text>
                </VStack>
                <Image
                  src={image}
                  alt={title}
                  w="80px"
                  h="90px"
                  objectFit="contain"
                />
              </HStack>
            )
          )}
        </Flex>

        <Heading
          color={'secondary.500'}
          textAlign="center"
          fontSize={{ base: '22px', lg: '2xl' }}
          mt={8}
          mb={4}
          fontWeight={400}
          maxW={'80%'}
          lineHeight={'1.25em'}
        >
          <Box as="span" color="#5299A9">
            94% of patients{' '}
          </Box>
          find symptom relief, often in just 7 days*
        </Heading>

        <Box w={{ base: '100%', lg: '342px' }}>
          <GradientButton
            onPress={() => onStartOnboarding(TRACKING_LANDING_PAGE_CTA)}
          >
            Start your Ayble Journey Today!
          </GradientButton>
        </Box>
      </VStack>
    </Box>
  );
};
